import * as FNS from "date-fns";
import {
	compareDesc,
	format,
	formatDistance,
	isValid,
	differenceInMilliseconds,
	differenceInSeconds,
	differenceInMinutes,
	differenceInHours,
	differenceInDays,
	startOfDay,
	addMilliseconds,
	parseISO,
	differenceInCalendarDays,
	set,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ru, uk, enUS as en, az, tr, ro, ka } from "date-fns/locale";
import { isBoolean } from "lodash";
import { TFunction } from "i18next";

import { Language } from "../services";
import { ConstantLanguage } from "../assets/languages/langs";

export const DaysGuard = (day?: number): day is DateFns.DaysNumber =>
	typeof day === "number" && day >= 0 && day <= 6;

export const ConstantFormat = {
	day: "eeee",
	month: "MMMM",
	year: "yyyy",

	MDYHMSA: "MM.dd.yyyy hh:mm:ss aa",
	MDYHMA: "MM.dd.yyyy hh:mm aa",
	MDY: "MM.dd.yyyy",
	HMSA: "hh:mm:ss aa",
	HMA: "hh:mm aa",
	HA: "hh aa",
	DMMYHMSA: "dd MMMM yyyy hh:mm:ss aa",

	MDYHMS_UK: "dd.MM.yyyy HH:mm:ss",
	MDYHM_UK: "dd.MM.yyyy HH:mm",
	DMMYHMS_UK: "dd MMMM yyyy HH:mm:ss",
	MDY_UK: "dd.MM.yyyy",
	HMS_UK: "HH:mm:ss",
	HM_UK: "HH:mm",
	H_UK: "HH",
	K_UK: "k",
	KK_UK: "kk",

	YOrder: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
	DMY_DASH: "dd-MM-yyyy",
	HM_DDMMYYYY_SLASH: "HH:mm dd/MM/yyyy",
	HMS_DDMMYYYY_SLASH: "HH:mm:ss dd/MM/yyyy",
} as const;

// ! Not use this format (in key object) for date-fns
const BadMomentFormats: Record<string, keyof typeof ConstantFormat> = {
	"DD-MM-YYYY": "DMY_DASH",
	"DD.MM.YYYY": "MDY_UK",
	"MM.DD.YYYY": "MDY",
	"HH:mm DD/MM/yyyy": "HM_DDMMYYYY_SLASH",
	"HH:mm:ss DD/MM/yyyy": "HMS_DDMMYYYY_SLASH",
};

// https://www.npmjs.com/package/date-fns-tz
// https://date-fns.org/v2.23.0/docs/format
export class DateFns {
	public ONE_DAY_MS: number = 1000 * 60 * 60 * 24;

	public year: number = new Date().getFullYear();

	public month: number = new Date().getMonth();

	public locales: Map<Language, Locale> = new Map<Language, Locale>();

	lang: Language = ConstantLanguage.UK;

	public fns = FNS;

	constructor(data: { lang?: Language } = { lang: ConstantLanguage.UK }) {
		this.locales.set(ConstantLanguage.UK, uk);
		this.locales.set(ConstantLanguage.RU, ru);
		this.locales.set(ConstantLanguage.EN, en);
		this.locales.set(ConstantLanguage.AZ, az);
		this.locales.set(ConstantLanguage.TR, tr);
		this.locales.set(ConstantLanguage.RO, ro);
		this.locales.set(ConstantLanguage.KA, ka);

		this.lang = data.lang || ConstantLanguage.UK;
		this.fns = FNS;
	}

	// ------------------------------------------------

	public setLang(lang: Language) {
		this.lang = lang;
	}

	public getMonths = (): string[] => {
		const months = Array.from(Array(this.month), (_, i) =>
			format(new Date(new Date().getFullYear(), i + 1, i + 1), "MMMM"),
		);
		return months;
	};

	public getAllMonths = (): string[] => {
		const months = Array.from(Array(12), (_, i) =>
			format(new Date(new Date().getFullYear(), i + 1, i + 1), "MMMM"),
		);
		return months;
	};

	public getDay = (): number =>
		Number(format(new Date(), "dd MMMM yyyy").substring(0, 2));

	public getDaysInWeek = (): string[] =>
		Array.from(Array(7), (_, i) =>
			format(
				new Date(
					new Date().getFullYear(),
					new Date().getMonth(),
					i + 1,
				),
				"dd",
			),
		);

	public getDaysInMonth = (
		month: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
		year: number = new Date().getFullYear(),
		type: "eeee" | "eee" | "do" | "d" | "dd" = "dd",
	): string[] => {
		const f = new Date(year, month);
		const days = format(f, "dd");
		const getMonth: number = month || new Date().getMonth();
		return Array.from(Array(Number(days)), (_, i) =>
			format(new Date(year, getMonth, i + 1), type),
		);
	};

	public getDayName = (
		type: "eeee" | "eee" | "eeeeee" | "eo" | "e" | "ee" = "ee",
	): number => Number(format(new Date(), type).substring(0, 2));

	public getDayNameByNumber = (
		weekStartsOn: DateFns.DaysNumber = 0,
		type: "eeee" | "eee" | "eeeeee" | "eo" | "e" | "ee" = "eeee",
	): string => {
		const locale = this.locale(this.lang);
		const start = this.fns.startOfWeek(new Date(), {
			weekStartsOn: 1,
			locale,
		});
		const day = this.fns.addDays(start, weekStartsOn);

		return format(day, type, { locale });
	};

	private locale = (str?: Language): Locale | undefined => {
		if (!str) return undefined;
		return this.locales.get(str);
	};

	/** * Get Year, month, day, hour, min, second   */
	public format = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? "MM.dd.yyyy hh:mm"
				: "dd.MM.yyyy HH:mm:ss";
		return format(date, formatTime, { locale });
	};

	/** * Get Year, month, day. */
	public format2 = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? ConstantFormat.MDY
				: ConstantFormat.MDY_UK;
		return format(date, formatTime, { locale });
	};

	/** * Get hour, min, second */
	public format3 = (
		date: Date | number | null | undefined,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";
		const locale = this.locale(lang);

		const formatTime =
			lang === ConstantLanguage.EN
				? ConstantFormat.HMSA
				: ConstantFormat.HMS_UK;
		return format(date, formatTime, { locale });
	};

	private formatHandleMoment = (str: string): DateFns.ValueFormat => {
		const key = BadMomentFormats[str as keyof typeof BadMomentFormats];
		if (key) {
			return ConstantFormat[key];
		}
		return str as DateFns.ValueFormat;
	};

	/**
	 * @deprecated This method is deprecated and will be removed in future versions.
	 * Please use `formatTime2` instead.
	 */
	public formatTime = (
		date: string | Date | number | null | undefined,
		formatTime: DateFns.ValueFormat,
		lang: Language = this.lang,
	): string => {
		if (date === null || date === undefined) return "";

		const _formatTime = this.formatHandleMoment(formatTime);

		const locale = this.locale(lang);

		if (typeof date === "string") {
			const time = new Date(date);
			if (!isValid(time)) return "";
			return format(time, _formatTime, { locale });
		}

		if (!isValid(date)) return "";
		return format(date, _formatTime, { locale }) ?? "";
	};

	public formatTime2 = (
		date: string | Date | number | null | undefined,
		formatTime: DateFns.ValueFormat,
		options?: {
			lang?: Language; // ? to change language
			timezone?: string; // ? analog moment(...).tz(timezoneName) // priority zone than toLocalTime
			toLocalTime?: boolean; // ? analog moment(...).local(true)
			zeroSeconds?: boolean; // ? analog moment(...).set({ second: 0, millisecond: 0 })
		},
	): string => {
		const { lang, toLocalTime, timezone, zeroSeconds } = options || {};

		if (date === null || date === undefined) return "";

		const _lang = lang || this.lang;
		const _formatTime = this.formatHandleMoment(formatTime);
		const locale = this.locale(_lang);

		let parsedDate: Date;

		if (typeof date === "string" || typeof date === "number") {
			parsedDate = new Date(date);
		} else {
			parsedDate = date;
		}

		if (!isValid(parsedDate)) return "";

		let finalDate = parsedDate;

		if (timezone) {
			finalDate = toZonedTime(parsedDate, timezone);
		} else if (toLocalTime) {
			const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
			finalDate = toZonedTime(parsedDate, localTz);
		}

		if (zeroSeconds) {
			finalDate = set(finalDate, { seconds: 0, milliseconds: 0 });
		}

		return format(finalDate, _formatTime, { locale }) ?? "";
	};

	public formatTimeFromMilliseconds = (
		date: number,
		formatTime: DateFns.ValueFormat,
		lang: Language = this.lang,
	): string => {
		const locale = this.locale(lang);
		const time = new Date(date);
		if (!isValid(time)) return "";
		return format(time, formatTime, { locale });
	};

	/**
	 * Getting the remaining time
	 *
	 * @param {(number | Date)} start
	 * @param {(number | Date)} end
	 * @param {?Language} [locale]
	 * @returns {string}
	 */
	public formatDistance = (
		start: number | Date,
		end: number | Date,
		locale?: Language,
	) => {
		const time = formatDistance(start, end, {
			locale: this.locale(locale),
		});
		return time;
	};

	/** * https://date-fns.org/v2.30.0/docs/compareDesc
	 ** now === to = 0
	 ** now < to  = 1
	 ** now > to  = -1
	 */
	public compareDesc = (
		from: number | Date,
		to: number | Date,
	): 0 | 1 | -1 => {
		const value = compareDesc(from, to);
		if (value === 1) return 1;
		if (value === 0) return 0;
		return -1;
	};

	/** * Sorts the array by the specified time */
	public isPeriod = (
		date: string | Date | null | undefined,
		period?: DateFns.Period,
	): boolean => {
		if (date === null || date === undefined) return false;
		const newDate = new Date(date);

		if (period?.from && period?.to) {
			const dateFrom = new Date(period.from);
			const dateTo = new Date(period.to);
			const isValidTo = this.compareDesc(dateFrom, newDate);
			const isValidFrom = this.compareDesc(newDate, dateTo);
			return isValidTo !== -1 && isValidFrom !== -1;
		}
		if (period?.from) {
			const from = new Date(period.from);
			const isValid = this.compareDesc(from, newDate);
			return isValid !== -1;
		}
		if (period?.to) {
			const to = new Date(period.to);
			const isValid = this.compareDesc(newDate, to);
			return isValid !== -1;
		}

		return true;
	};

	/**
	 * Formats a date string to a human-readable format.
	 * @param dateString - The date string to format.
	 * @param badValue - The value to return if the date string is invalid.
	 * @returns The formatted date string or the badValue if invalid.
	 */

	public getCurrentPeriod = (
		startDate: number | Date,
		periodDays: number,
	): {
		from: number;
		to: number;
	} => {
		const now = new Date().getTime();
		const startDateMs = new Date(startDate).getTime();

		const differenceDays = (now - startDateMs) / this.ONE_DAY_MS;
		const periodsFloor =
			Math.floor(differenceDays / periodDays) * periodDays;
		const periodsCeil = Math.ceil(differenceDays / periodDays) * periodDays;
		const from = startDateMs + periodsFloor * this.ONE_DAY_MS;
		const to = startDateMs + periodsCeil * this.ONE_DAY_MS;

		return { from, to };
	};

	public millisecondsToDate = (
		timestamp: number,
		options: {
			/** timezone + or - */
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus, utc = false } = options;
		const newDate = startOfDay(new Date());
		const timezone = this.getTimezone();

		const timePlus =
			!utc && isBoolean(plus) && plus ? timestamp + timezone : timestamp;

		const time = addMilliseconds(newDate, timePlus);

		if (utc) {
			const value = plus
				? time.getTime() + timezone
				: time.getTime() - timezone;

			const utcTime = new Date(value);
			return utcTime;
		}

		return time;
	};

	public millisecondsFromDate = (
		date: Date,
		options: {
			plus?: boolean;
			utc?: boolean;
		},
	) => {
		const { plus = true, utc = false } = options;
		let givenDate = date;
		const timezone = this.getTimezone();

		if (utc) {
			const value = plus
				? date.getTime() + timezone
				: date.getTime() - timezone;
			givenDate = new Date(value);
		}

		const startOfGivenDay = startOfDay(givenDate);
		const milliseconds = differenceInMilliseconds(
			givenDate,
			startOfGivenDay,
		);

		if (!utc && isBoolean(plus) && !plus) {
			return milliseconds - timezone;
		}

		return milliseconds;
	};

	public getUTCTime = (
		date: Date | number | string,
		options: {
			ms?: boolean;
			plus?: boolean;
			utc?: boolean;
			iso?: boolean;
			isFns?: boolean;
		},
	): number | Date | string => {
		const { ms = false, plus = true, iso = false, isFns = false } = options;
		const dateTime = new Date(date).getTime();
		const zone = this.getTimezone();

		if (isFns) return format(dateTime, ConstantFormat.YOrder);
		if (plus) {
			const time = dateTime + zone;

			if (ms) return time;
			if (iso) return new Date(time).toISOString();

			return new Date(time);
		}

		const time = dateTime - zone;

		if (ms) return time;
		if (iso) return new Date(time).toISOString();

		return new Date(time);
	};

	public getTimezone = (): number => {
		const time = Math.abs(new Date().getTimezoneOffset() * 60_000);
		return time;
	};

	public startOfDay(date: Date = new Date()) {
		return this.fns.startOfDay(date);
	}

	public endOfDay(date: Date = new Date()) {
		return this.fns.endOfDay(date);
	}

	public getMillisecondsDiff = (
		start: Date | number,
		end: Date | number,
	): number => {
		const s = new Date(start);
		const e = new Date(end);

		if (!isValid(s) || !isValid(e)) return 0;
		return differenceInMilliseconds(s, e);
	};

	public getDiff = (
		from: Date | string | number,
		to: Date | string | number = Date.now(),
		unit:
			| "milliseconds"
			| "seconds"
			| "minutes"
			| "hours"
			| "days" = "milliseconds",
	): number => {
		const date1 = new Date(from);
		const date2 = new Date(to);

		if (!isValid(date1) || !isValid(date2)) return 0;

		switch (unit) {
			case "seconds":
				return differenceInSeconds(date1, date2);
			case "minutes":
				return differenceInMinutes(date1, date2);
			case "hours":
				return differenceInHours(date1, date2);
			case "days":
				return differenceInDays(date1, date2);
			default:
				return differenceInMilliseconds(date1, date2);
		}
	};

	/**
	 * Converts a date string to time in hours and minutes format.
	 * @param dateString - The date string to convert.
	 * @returns The time in hours and minutes (e.g., "15:30") or empty string if invalid.
	 */
	public toClockFaceTime = (
		dateString: string | null | undefined,
		badValue: string | null | undefined = "",
	): string | null => {
		if (!dateString) {
			return badValue;
		}

		const date = new Date(dateString);

		if (!isValid(date)) {
			return badValue;
		}

		return format(date, ConstantFormat.HM_UK);
	};

	/**
	 * Formats a date string to a human-readable format similar to MomentFnc.toDateNavigator.
	 * @param args - The arguments object containing the date string and the bad value.
	 * @returns The formatted date string or the badValue if invalid.
	 */
	public toDateNavigator = ({
		t,
		dateToFormat,
		badValue = "",
		formatVersion = "",
	}: DateFns.ToDateNavigator): string | null => {
		if (!dateToFormat) {
			return badValue;
		}

		const date = parseISO(dateToFormat);

		if (!isValid(date)) {
			return badValue;
		}

		const today = startOfDay(new Date());
		const targetDay = startOfDay(date);
		const diffInDays = differenceInCalendarDays(targetDay, today);

		let formatFinish: string | undefined;

		const todayT = t("dates.today") ?? "Today";
		const yesterdayT = t("dates.yesterday") ?? "Yesterday";
		const tomorrowT = t("dates.tomorrow") ?? "Tomorrow";

		if (formatVersion === "format1") {
			// diffInDays === 0 — сегодня
			// diffInDays === 1 — завтра (tomorrow)
			// diffInDays === -1 — вчера (yesterday)
			formatFinish = new Map<number, string>([
				[0, todayT],
				[1, tomorrowT],
				[-1, yesterdayT],
			]).get(diffInDays);
		}

		if (formatVersion === "format2") {
			formatFinish = diffInDays === 0 ? "" : undefined;
		}

		const retval = formatFinish ?? format(date, ConstantFormat.MDY_UK);

		return retval;
	};
}

export declare namespace DateFns {
	type Period = {
		from?: Date;
		to?: Date;
	};

	type TypeFormat = typeof ConstantFormat;

	type ValueFormat = (typeof ConstantFormat)[keyof typeof ConstantFormat];

	type DaysNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;

	interface ToDateNavigator {
		t: TFunction;
		dateToFormat: string | null | undefined;
		badValue?: string | null | undefined;
		formatVersion?: string | null | undefined;
	}
}

/**
 * Moment.js → Date-fns форматирование дат
 * ----------------------------------------
 * Используй эту таблицу для быстрой замены форматов при миграции с moment.js
 * Обрати внимание: date-fns чувствителен к регистру символов!
 *
 * ┌──────────────────────┬────────────────────────────┐
 * │     Moment.js        │        date-fns            │
 * ├──────────────────────┼────────────────────────────┤
 * │ YYYY (год)           │ yyyy                       │
 * │ YY (2 цифры года)    │ yy                         │
 * │ M (месяц)            │ M                          │
 * │ MM (месяц с 0)       │ MM                         │
 * │ MMM (сокр. месяц)    │ MMM                        │
 * │ MMMM (полный месяц)  │ MMMM                       │
 * │ D (день)             │ d                          │
 * │ DD (день с 0)        │ dd                         │
 * │ d (день недели от 0) │ i (1–7, ISO, понедельник)  │
 * │ E (день недели)      │ e (0–6, местный, вс – 0)    │
 * │ H (часы 0–23)        │ H                          │
 * │ HH (часы с 0)        │ HH                         │
 * │ h (часы 1–12)        │ h                          │
 * │ hh (часы 1–12 с 0)   │ hh                         │
 * │ m (минуты)           │ m                          │
 * │ mm (минуты с 0)      │ mm                         │
 * │ s (секунды)          │ s                          │
 * │ ss (секунды с 0)     │ ss                         │
 * │ a (am/pm)            │ aa                         │
 * │ A (AM/PM)            │ aa                         │
 * │ Z (зона смещения)    │ XXX                        │
 * │ ZZ (зона числом)     │ xx                         │
 * │ X (timestamp в сек)  │ t                          │
 * │ x (timestamp в мс)   │ T                          │
 * └──────────────────────┴────────────────────────────┘
 *
 * Пример:
 * moment:     moment(date).format("DD.MM.YYYY HH:mm:ss")
 * date-fns:   format(date, "dd.MM.yyyy HH:mm:ss")
 */
